import type { CheckoutSession, PaymentOption, SavedMethodOneTap, SavedPaymentMethod } from '@/lib/types';
import { CheckoutHeader } from '@/components/CheckoutHeader';

function PaymentIcon({ icon }: { icon?: string }) {
  const cls = 'w-8 h-8 flex-shrink-0 text-gray-500';
  switch (icon) {
    case 'card':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth={1.5} />
          <path strokeWidth={1.5} d="M2 10h20" />
        </svg>
      );
    case 'wallet':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
          <path strokeWidth={1.5} strokeLinecap="round" d="M16 12h2" />
        </svg>
      );
    case 'qr':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth={1.5} />
          <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth={1.5} />
          <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth={1.5} />
          <path strokeWidth={1.5} strokeLinecap="round" d="M14 14h2M14 17h3M17 14v3M20 17v3M17 20h3" />
        </svg>
      );
    case 'bank':
    case 'bank_transfer':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            d="M3 21h18M3 10h18M5 10V7M19 10V7M12 10V7M3 7l9-4 9 4" />
          <rect x="5" y="10" width="3" height="7" strokeWidth={1.5} />
          <rect x="10.5" y="10" width="3" height="7" strokeWidth={1.5} />
          <rect x="16" y="10" width="3" height="7" strokeWidth={1.5} />
        </svg>
      );
    default:
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
          <path strokeWidth={1.5} strokeLinecap="round" d="M12 8v4M12 16h.01" />
        </svg>
      );
  }
}

function BrandBadge({ brand }: { brand?: string }) {
  if (!brand) return null;
  const label = brand.toUpperCase().slice(0, 4);
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 tracking-wider">
      {label}
    </span>
  );
}

function channelLabel(channel: string): string {
  if (channel === 'qr') return 'Código QR';
  if (channel === 'reference') return 'Referencia';
  return channel;
}

function fmtCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

interface Props {
  session: CheckoutSession;
  paymentOptions: PaymentOption[];
  savedMethodOneTap?: SavedMethodOneTap | null;
  onSelect: (methodKey: string, channelType?: string) => void;
  onSelectSaved: (methodKey: string, paymentMethodId: string, channelType?: string) => void;
  onCancel: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  card: 'Tarjeta',
  wallet: 'Billetera',
  bank_transfer: 'Transferencia bancaria',
  cash: 'Efectivo',
  buy_now_pay_later: 'Pagar después',
  crypto: 'Criptomoneda',
};

function sortSavedMethods(methods: SavedPaymentMethod[]): SavedPaymentMethod[] {
  return [...methods].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return 0;
  });
}

export function MethodSelection({ session, paymentOptions, savedMethodOneTap, onSelect, onSelectSaved, onCancel }: Props) {
  // Hide options that require a tokenization SDK — no raw card input allowed (PCI boundary).
  // Exception: card options WITH a tokenizationConfig are shown — they enter the Wompi tokenization flow.
  // [PR-14.25 / INVARIANT 6] Card visibility comes from resolver (tokenizationConfig present = tokenization available).
  const visibleOptions = paymentOptions.filter(
    (o) => !o.requiresTokenization || !!o.tokenizationConfig
  );

  const grouped = visibleOptions.reduce<Record<string, PaymentOption[]>>((acc, opt) => {
    const cat = opt.methodType || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(opt);
    return acc;
  }, {});

  const walletBalance = session.wallet_balance
    ? new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: session.currency,
        minimumFractionDigits: 0,
      }).format(parseFloat(session.wallet_balance))
    : null;

  const hasItems = Array.isArray(session.items) && session.items.length > 0;

  // Find the option that matches the one-tap method (for methodKey resolution)
  const oneTapOption = savedMethodOneTap
    ? (visibleOptions.find((o) => o.methodType === savedMethodOneTap.methodType) ??
       visibleOptions.find((o) => o.methodKey.includes('card')))
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-8 pb-8">
      <div className="w-full max-w-md">
        {/* Merchant + amount header */}
        <CheckoutHeader session={session} />

        {/* Line items card — product-backed sessions only, snapshot values */}
        {hasItems && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {session.items!.length === 1 ? 'Producto' : `${session.items!.length} productos`}
            </p>
            {session.items!.slice(0, 3).map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-3 py-2 border-b last:border-0 border-gray-100"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 font-medium truncate">{item.product_name}</p>
                  {item.price_label && (
                    <p className="text-xs text-gray-400">{item.price_label}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    Cant. {item.quantity}
                    {item.quantity > 1 && (
                      <> · {fmtCurrency(item.unit_amount, item.currency)} c/u</>
                    )}
                  </p>
                </div>
                <p className="text-sm font-medium text-gray-900 font-mono flex-shrink-0">
                  {fmtCurrency(item.amount_subtotal, item.currency)}
                </p>
              </div>
            ))}
            {session.items!.length > 3 && (
              <p className="text-xs text-gray-400 mt-2">
                +{session.items!.length - 3} producto{session.items!.length - 3 !== 1 ? 's' : ''} más
              </p>
            )}
            <p className="text-xs text-gray-400 mt-3">Precios al momento de creación del pedido</p>
          </div>
        )}

        {/* One-tap saved method banner */}
        {savedMethodOneTap && oneTapOption && (
          <div className="bg-white rounded-2xl shadow-sm border border-brand-200 p-5 mb-4">
            <p className="text-xs font-semibold text-brand-600 uppercase tracking-wide mb-3">
              Pago rápido
            </p>
            <button
              onClick={() => onSelectSaved(oneTapOption.methodKey, savedMethodOneTap.paymentMethodId)}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-brand-50 border-2 border-brand-400 hover:border-brand-500 hover:bg-brand-100 transition-colors text-left min-h-[60px]"
            >
              <PaymentIcon icon={savedMethodOneTap.methodType ?? oneTapOption.methodType} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <BrandBadge brand={savedMethodOneTap.brand} />
                  <p className="font-medium text-gray-900 truncate">{savedMethodOneTap.displayLabel}</p>
                </div>
                {savedMethodOneTap.last4 && (
                  <p className="text-xs text-gray-500">•••• {savedMethodOneTap.last4} · guardada</p>
                )}
              </div>
              <svg className="w-5 h-5 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Payment methods */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {savedMethodOneTap ? 'Otros métodos de pago' : 'Selecciona un método de pago'}
          </h2>

          {visibleOptions.length === 0 && (
            <p className="text-gray-500 text-center py-4">No hay métodos de pago disponibles.</p>
          )}

          {Object.entries(grouped).map(([category, options]) => (
            <div key={category} className="mb-5 last:mb-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {CATEGORY_LABELS[category] ?? category}
              </p>
              <div className="space-y-2">
                {options.map((opt) => {
                  const hasSaved = (opt.savedMethods?.length ?? 0) > 0;

                  if (hasSaved) {
                    return (
                      <OptionWithSavedMethods
                        key={opt.methodKey}
                        opt={opt}
                        onSelect={onSelect}
                        onSelectSaved={onSelectSaved}
                      />
                    );
                  }

                  if ((opt.channels?.length ?? 0) > 1) {
                    return (
                      <div key={opt.methodKey} className="p-4 rounded-xl border-2 border-gray-100">
                        <div className="flex items-center gap-3 mb-3">
                          <PaymentIcon icon={opt.methodType} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{opt.label}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {opt.channels!.map((channel) => (
                            <button
                              key={channel}
                              onClick={() => onSelect(opt.methodKey, channel)}
                              className="flex-1 py-2.5 px-3 rounded-lg border-2 border-gray-200 hover:border-brand-500 hover:bg-brand-50 text-sm font-medium text-gray-700 transition-colors min-h-[44px]"
                            >
                              {channelLabel(channel)}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={opt.methodKey}
                      onClick={() => onSelect(opt.methodKey)}
                      className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-gray-100 hover:border-brand-500 hover:bg-brand-50 transition-colors text-left min-h-[60px]"
                    >
                      <PaymentIcon icon={opt.methodType} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{opt.label}</p>
                        {opt.methodKey === 'platform_wallet' && walletBalance && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {walletBalance} disponible
                          </p>
                        )}
                      </div>
                      <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Cancel */}
        <button
          onClick={onCancel}
          className="w-full mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors py-3 min-h-[44px]"
        >
          Cancelar y volver
        </button>

        <p className="text-center text-xs text-gray-400 mt-2 mb-2">
          Pago procesado de forma segura por Orangepill
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Option with saved methods — radio-style rows + "new" row at bottom
// ---------------------------------------------------------------------------

interface OptionWithSavedMethodsProps {
  opt: PaymentOption;
  onSelect: (methodKey: string, channelType?: string) => void;
  onSelectSaved: (methodKey: string, paymentMethodId: string, channelType?: string) => void;
}

function OptionWithSavedMethods({ opt, onSelect, onSelectSaved }: OptionWithSavedMethodsProps) {
  const sorted = sortSavedMethods(opt.savedMethods ?? []);
  const newLabel = opt.methodType === 'card' ? 'Usar nueva tarjeta' : 'Usar nuevo método';

  return (
    <div className="rounded-xl border-2 border-gray-100 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <PaymentIcon icon={opt.methodType} />
        <p className="font-medium text-gray-900 text-sm">{opt.label}</p>
      </div>

      {/* Saved method rows */}
      {sorted.map((saved) => (
        <button
          key={saved.paymentMethodId}
          onClick={() => onSelectSaved(opt.methodKey, saved.paymentMethodId)}
          className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 hover:bg-brand-50 transition-colors text-left min-h-[52px]"
        >
          <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0 flex items-center justify-center">
            {saved.isDefault && <div className="w-2 h-2 rounded-full bg-brand-500" />}
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <BrandBadge brand={saved.brand} />
            <span className="text-sm text-gray-900 font-mono">
              {saved.last4 ? `•••• ${saved.last4}` : saved.displayLabel}
            </span>
            <span className="text-xs text-gray-400">(guardada)</span>
          </div>
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ))}

      {/* New method row */}
      <button
        onClick={() => onSelect(opt.methodKey)}
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left min-h-[52px]"
      >
        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
        <span className="text-sm text-gray-600 font-medium">{newLabel}</span>
        <svg className="w-4 h-4 text-gray-300 flex-shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
